import React, { forwardRef, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { lightTheme } from '@/customTheme'
import FloatingInspectorButtonIcon, {
  FloatingInspectorButtonIconProps,
} from '@/scenes/Editor/components/FloatingInspectorButton/FloatingInspectorButtons/FloatingInspectorButtonIcon'
import { FloatingInspectorButtonHandle } from '@/scenes/Editor/components/FloatingInspectorButton/FloatingInspectorButton'
import {
  BeforeAfterToggleButton,
  BeforeAfterToggleButtonProps,
} from '@/scenes/Editor/components/BeforeAfter/BeforeAfterToggleButton'
import DownloadPopover from '@/scenes/Editor/components/FloatingInspectorButton/SharedComponents/Popover/DownloadPopover/DownloadPopover'
import Download from '@/scenes/Editor/components/Icons/Download'
import Pencil from '@/scenes/Editor/components/Icons/Pencil'
import { CARD_ICON_BUTTON_STYLE, CARD_ICON_BUTTON_STYLE_PLAIN } from './GeneratedCard.styles'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { selectUserIsPremium } from '@/store/slices/user/selectors'
import { customAmplitude } from '@/utils/customAmplitude'

/**
 * Button presets used by GeneratedCard's standard action set, and exported so a
 * caller can also drop them (or any custom node) into the `extraButtons` slots
 * — GeneratedCard lays the nodes out, the presets own each button's UX.
 */

// Tracks the close function of the currently open download popover so only one is open at a time.
let closeCurrentDownloadPopover: (() => void) | null = null

/** Generic AI-glow icon button wired with the shared card styling. */
export const CardIconButton = forwardRef<FloatingInspectorButtonHandle, FloatingInspectorButtonIconProps>(
  (props, ref) => <FloatingInspectorButtonIcon ref={ref} style={CARD_ICON_BUTTON_STYLE} {...props} />
)

/** "Open in editor" pencil button. `variant="plain"` drops the AI glow for a flat black shadow (batch tiles). */
export function EditCardButton({
  onClick,
  tooltipText = 'Open in editor',
  variant = 'blue-purple-glow',
}: {
  onClick: () => void
  tooltipText?: string
  variant?: 'blue-purple-glow' | 'plain'
}) {
  return (
    <CardIconButton
      icon={<Pencil size={24} fill={lightTheme.colors.blackGray} />}
      onClick={onClick}
      tooltipText={tooltipText}
      placementTooltip="top"
      {...(variant === 'plain' ? { style: CARD_ICON_BUTTON_STYLE_PLAIN } : {})}
    />
  )
}

interface DownloadCardButtonProps {
  /** Direct-download handler (used when no popover scope is supplied). */
  onClick?: () => void
  /** Stable getter for the popover's download scope. When set, a DownloadPopover is shown. */
  downloadScope?: () => string[] | Promise<string[]>
  source?: string
  destination?: string
  tooltipText?: string
  /** Initial format key for the DownloadPopover when no editor is present. */
  defaultFormatKey?: string
  /** Premium source key: gates the download button inside the popover. */
  downloadButtonOpenPremiumPrompt?: string
  /** Premium source key: gates this button itself — non-premium users see the premium modal instead of the popover. */
  openOnButtonClickPremiumPrompt?: string
  showWatermarkButton?: boolean
  onPopoverOpenChange?: (isOpen: boolean) => void
}

/** Download button — opens a DownloadPopover when `downloadScope` is given, otherwise fires `onClick`. */
export function DownloadCardButton({
  onClick,
  downloadScope,
  source,
  destination,
  tooltipText = 'Download',
  defaultFormatKey,
  downloadButtonOpenPremiumPrompt,
  openOnButtonClickPremiumPrompt,
  showWatermarkButton,
  onPopoverOpenChange,
}: DownloadCardButtonProps) {
  const buttonRef = useRef<FloatingInspectorButtonHandle>(null)
  // Stable reference so we can identity-compare against the singleton.
  const myCloseRef = useRef(() => buttonRef.current?.closePopover())
  const hasPremium = useSelector(selectUserIsPremium)
  const dispatch = useDispatch()

  const premiumGated = !!openOnButtonClickPremiumPrompt && !hasPremium
  const handlePremiumGatedClick = () => {
    dispatch(setOpenModalTryPremium({ isOpen: true, source: openOnButtonClickPremiumPrompt! }))
    customAmplitude('Premium Prompt', { Source: openOnButtonClickPremiumPrompt, Type: 'Standard' })
  }

  return (
    <CardIconButton
      ref={buttonRef}
      icon={<Download size={24} fill={lightTheme.colors.blackGray} />}
      onClick={premiumGated ? handlePremiumGatedClick : (onClick ?? (() => {}))}
      tooltipText={tooltipText}
      placementTooltip="top"
      onPopoverOpenChange={(isOpen) => {
        if (isOpen) {
          // Register this button first, then close the previous one.
          // (ordering matters: the previous button's onClose will check the singleton
          //  and must see this button already registered so it doesn't clear our entry)
          const prevClose = closeCurrentDownloadPopover
          closeCurrentDownloadPopover = myCloseRef.current
          prevClose?.()
        } else if (closeCurrentDownloadPopover === myCloseRef.current) {
          // Only clear when this button is still the registered one.
          closeCurrentDownloadPopover = null
        }
        onPopoverOpenChange?.(isOpen)
      }}
      popoverContent={
        downloadScope && !premiumGated
          ? (buttonRef, closePopover) => (
              <DownloadPopover
                onClose={() => closePopover?.()}
                buttonRef={buttonRef}
                placement="bottom"
                downloadScope={downloadScope}
                source={source ?? ''}
                destination={destination ?? ''}
                defaultFormatKey={defaultFormatKey}
                downloadButtonOpenPremiumPrompt={downloadButtonOpenPremiumPrompt}
                showWatermarkButton={showWatermarkButton}
              />
            )
          : undefined
      }
    />
  )
}

/** Press-and-hold before/after toggle. */
export function BeforeAfterCardButton(props: BeforeAfterToggleButtonProps) {
  return <BeforeAfterToggleButton {...props} />
}
